import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, TableCell } from "@mui/material";
import { textStyle } from "components/UI/dynamicTable/DynamicTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  getBaseKpiTypes,
  getMasterKpiData,
  getMasterKpiFilteredData,
  getMasterKpiMetaData,
  setColumnVisibility,
  updateBaseKpiTypes,
  updateColumnVisibility,
  setColumnOrder,
  updateColumnOrder,
} from "actions/dataHub/dataHubAction";
import { queryClient } from "index";

export const useDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDrawer = () => setIsOpen((prev) => !prev);
  return { isOpen, toggleDrawer };
};

export const useDataHubKpiMasterData = (
  searchQuery = "",
  selectedKpi,
  page,
  filters
) => {
  const tableRef = useRef(null);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const context = "kpiMaster";

  const { columnVisibility, columnOrder } = useSelector(
    (state) => state?.dataset
  );

  const currentColumnVisibility = columnVisibility[context] || {};
  const currentColumnOrder = columnOrder[context] || [];

  // Drawers
  const { isOpen: optionDrawer, toggleDrawer: optionToggleDrawer } =
    useDrawer();
  const { isOpen: assignDrawer, toggleDrawer: assignToggleDrawer } =
    useDrawer();
  const { isOpen: exploreDrawer, toggleDrawer: exploreToggleDrawer } =
    useDrawer();
  const { isOpen: omissionDrawer, toggleDrawer: omissionToggleDrawer } =
    useDrawer();
  const {
    isOpen: KPIGroupNameDrawerOpen,
    toggleDrawer: KPIGroupNameToggleDrawer,
  } = useDrawer();
  const {
    isOpen: actionSetingDrawerOpen,
    toggleDrawer: actionSetingToggleDrawer,
  } = useDrawer();

  // Get Kpi Master Meta Data for Filters
  const { data: masterKpiMetaData } = useQuery({
    queryKey: ["getMasterKpiMetaData"],
    queryFn: () => getMasterKpiMetaData(),
    staleTime: Infinity,
    gcTime: 0,
  });

  const shouldUseFilteredData = Object.keys(filters).some((key) => {
    if (Array.isArray(filters[key])) {
      return filters[key].length > 0;
    }
    return !!filters[key];
  });

  // Get Kpi Master Data
  const {
    data: { result: masterKpiData = [], total: totalKpiMaster } = {},
    isLoading: ismasterKpiDataLoading,
  } = useQuery({
    queryKey: ["getMasterKpiData", page, searchQuery],
    queryFn: () =>
      getMasterKpiData({
        starting_after: page === 1 ? 0 : (page - 1) * 10,
        limit: 15,
        keyword: searchQuery,
      }),
    staleTime: Infinity,
    enabled: !shouldUseFilteredData,
    cacheTime: 0,
    refetchIntervalInBackground: true,
  });

  // Get Kpi Master Filtered Data
  const {
    data: { result: masterKpiFilteredData = [], total: totalKpiFiltered } = {},
    isLoading: ismasterKpiFilteredLoading,
  } = useQuery({
    queryKey: ["getMasterKpiFilteredData", page, filters, searchQuery],
    queryFn: () =>
      getMasterKpiFilteredData({
        starting_after: page === 1 ? 0 : (page - 1) * 10,
        limit: 15,
        keyword: searchQuery,
        filters: filters,
      }),
    enabled: shouldUseFilteredData,
    staleTime: Infinity,
    cacheTime: 0,
    refetchIntervalInBackground: true,
  });

  // Mutations
  const handleUpdateBaseKpiTypes = useMutation({
    mutationFn: updateBaseKpiTypes,
    onSuccess: () => {
      queryClient.invalidateQueries(["baseKpiTypeData"]);
      queryClient.invalidateQueries(["getMasterKpiDataGrouping"]);
    },
    onError: (error) => {
      console.error("Create failed:", error);
    },
  });

  const handleGetBaseKpi = useMutation({
    mutationFn: getBaseKpiTypes,
    onError: (error) => {
      console.error("Create failed:", error);
    },
  });

  const { selectedRowData } = useSelector((state) => state.escCompliance);
  const [selectedOption, setSelectedOption] = useState("");

  const defaultItems = [
    {
      id: "1",
      text: "KPI ID",
      hide: false,
      alwaysVisible: true,
      tableCell: ({ kpiId = "" }) => (
        <TableCell style={textStyle("main")}>{kpiId || "-"}</TableCell>
      ),
    },
    {
      id: "2",
      text: "KPI Name",
      hide: false,
      alwaysVisible: true,
      tableCell: ({ kpiName }) => (
        <TableCell style={textStyle()}>{kpiName || "-"}</TableCell>
      ),
    },
    {
      id: "3",
      text: "KPI Description",
      hide: false,
      tableCell: ({ description = "" }) => (
        <TableCell style={textStyle()}>{description || "-"}</TableCell>
      ),
    },
    {
      id: "9",
      text: "Category",
      hide: false,
      tableCell: ({ Sub_Section_2 = "" }) => (
        <TableCell style={textStyle()}>{Sub_Section_2 || "-"}</TableCell>
      ),
    },
    {
      id: "10",
      text: "Sub Category",
      hide: false,
      tableCell: ({ Sub_Section_2 = "" }) => (
        <TableCell style={textStyle()}>{Sub_Section_2 || "-"}</TableCell>
      ),
    },
    {
      id: "4",
      text: "KPI Type",
      hide: false,
      tableCell: ({ type }) => (
        <TableCell style={textStyle()}>
          <Chip
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.lighter,
              padding: "6px 10px",
              fontSize: "12px",
              color: theme.palette.primary.dark,
              borderRadius: "80px",
            })}
            label={type}
            size="small"
          />
        </TableCell>
      ),
    },
    {
      id: "5",
      text: "Unit Measurement",
      hide: false,
      tableCell: ({ unit }) => (
        <TableCell style={textStyle()}>{unit || "-"}</TableCell>
      ),
    },
    {
      id: "6",
      text: "Data Rollup",
      hide: false,
      tableCell: ({ dataRollup }) => (
        <TableCell style={textStyle()}>{dataRollup || "-"}</TableCell>
      ),
    },
    {
      id: "7",
      text: "KPI Group Name",
      hide: false,
      tableCell: ({ group }) => (
        <TableCell style={textStyle()}>{group || "-"}</TableCell>
      ),
    },
    {
      id: "8",
      text: "Action",
      hide: false,
      alwaysVisible: true,
      tableCell: ({ action }) => (
        <TableCell style={textStyle()}>
          <Box display={"flex"}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                exploreToggleDrawer();
              }}
            >
              {action}
            </Button>
          </Box>
        </TableCell>
      ),
    },
  ];

  const [items, setItems] = useState(defaultItems);

  // Load saved column visibility and order from Redux
  useEffect(() => {
    const loadedItems = [...defaultItems];

    if (currentColumnOrder.length > 0) {
      loadedItems.sort((a, b) => {
        const indexA = currentColumnOrder.indexOf(a.id);
        const indexB = currentColumnOrder.indexOf(b.id);
        return indexA - indexB;
      });
    }

    if (Object.keys(currentColumnVisibility).length > 0) {
      loadedItems.forEach((item) => {
        if (currentColumnVisibility[item.id] !== undefined) {
          item.hide = currentColumnVisibility[item.id];
        }
      });
    }

    setItems(loadedItems);
  }, [currentColumnVisibility, currentColumnOrder]);

  // const handleHideStatus = useCallback(
  //   (id) => {
  //     const column = items.find((item) => item.id === id);
  //     if (column && column.alwaysVisible) return;

  //     setItems((prev) => {
  //       const updatedItems = prev.map((item) =>
  //         item.id === id ? { ...item, hide: !item.hide } : item
  //       );

  //       const updatedItem = updatedItems.find((item) => item.id === id);
  //       if (updatedItem) {
  //         dispatch(updateColumnVisibility(context, id, updatedItem.hide));
  //       }

  //       return updatedItems;
  //     });
  //   },
  //   [dispatch, items, context]
  // );

  // const handleHideAllStatus = useCallback(
  //   (status) => {
  //     const shouldHide = status === "Unhide All";

  //     setItems((prev) => {
  //       const updatedItems = prev.map((item) => ({
  //         ...item,
  //         hide: item.alwaysVisible ? false : shouldHide,
  //       }));

  //       updatedItems.forEach((item) => {
  //         if (!item.alwaysVisible) {
  //           dispatch(updateColumnVisibility(context, item.id, item.hide));
  //         }
  //       });

  //       return updatedItems;
  //     });
  //   },
  //   [dispatch, context]
  // );

  // useEffect(() => {
  //   if (Object.keys(currentColumnVisibility).length === 0 && items.length > 0) {
  //     const initialVisibility = {};
  //     const initialOrder = [];

  //     items.forEach((item) => {
  //       initialVisibility[item.id] = item.hide || false;
  //       initialOrder.push(item.id);
  //     });

  //     dispatch(setColumnVisibility(context, initialVisibility));
  //     dispatch(setColumnOrder(context, initialOrder));
  //   }
  // }, [items, currentColumnVisibility, dispatch, context]);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const handleDragStart = (index) => {
    if (index === 0 || index === 1 || index === items.length - 1) return;
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    if (index === 0 || index === 1 || index === items.length - 1) return;
    dragOverItem.current = index;
  };

  const handleDrop = () => {
    if (
      dragItem.current === null ||
      dragOverItem.current === null ||
      dragItem.current === dragOverItem.current
    )
      return;
    if (
      dragOverItem.current === 0 ||
      dragOverItem.current === 1 ||
      dragOverItem.current === items.length - 1
    )
      return;

    setItems((prev) => {
      const copiedItems = [...prev];
      const draggedItem = copiedItems[dragItem.current];
      copiedItems.splice(dragItem.current, 1);
      copiedItems.splice(dragOverItem.current, 0, draggedItem);

      const newOrder = copiedItems.map((item) => item.id);
      dispatch(updateColumnOrder(context, newOrder));

      dragItem.current = null;
      dragOverItem.current = null;
      return copiedItems;
    });
  };

  const handleSelectedOption = (option) => setSelectedOption(option);

  return {
    optionDrawer,
    assignDrawer,
    exploreDrawer,
    omissionDrawer,
    KPIGroupNameDrawerOpen,
    actionSetingDrawerOpen,
    selectedOption,
    selectedRowData,
    items,
    masterKpiMetaData,
    masterKpiData: shouldUseFilteredData
      ? masterKpiFilteredData
      : masterKpiData,
    ismasterKpiDataLoading: shouldUseFilteredData
      ? ismasterKpiFilteredLoading
      : ismasterKpiDataLoading,
    handleUpdateBaseKpiTypes,
    handleGetBaseKpi,
    totalKpiMaster: shouldUseFilteredData ? totalKpiFiltered : totalKpiMaster,
    isLoading: shouldUseFilteredData
      ? ismasterKpiFilteredLoading
      : ismasterKpiDataLoading,
    isFiltered: shouldUseFilteredData,
    handleDragStart,
    handleDragEnter,
    handleDrop,
    // handleHideStatus,
    // handleHideAllStatus,
    handleSelectedOption,
    optionToggleDrawer,
    assignToggleDrawer,
    exploreToggleDrawer,
    omissionToggleDrawer,
    KPIGroupNameToggleDrawer,
    actionSetingToggleDrawer,
    tableRef,
    anchorEl,
    setAnchorEl,
  };
};
